/**
 * Catálogo hanging: seeds base + protocolos de sesión (memoria).
 * Persistencia local / sync plataforma = fases siguientes.
 */

import { HANGING_SEEDS } from './hangingProtocols'

/**
 * @param {string|null|undefined} text
 * @returns {string[]}
 */
export function csvToList(text) {
  return String(text || '')
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

/**
 * @param {string[]|null|undefined} list
 * @returns {string}
 */
export function listToCsv(list) {
  if (!Array.isArray(list) || !list.length) return ''
  return list.join(', ')
}

/**
 * @param {string|null|undefined} id
 * @returns {string}
 */
export function slugProtocolId(id) {
  return String(id || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9._-]/g, '')
    .slice(0, 80)
}

/**
 * @returns {import('./hangingProtocols').HangingProtocol}
 */
export function createEmptyProtocol() {
  const id = `custom-${Date.now().toString(36)}`
  return {
    id,
    label: 'Custom 1×1',
    priority: 55,
    match: { modalities: ['CT'], studyDescription: [] },
    layout: [1, 1],
    compareMode: false,
    linkSeries: false,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: {
          modality: 'CT',
          description: [],
          exclude: ['scout', 'ref', 'localizer', 'topogram', 'doc'],
          requireDifferentPlane: false,
        },
      },
    ],
  }
}

/**
 * @param {import('./hangingProtocols').HangingProtocol|null|undefined} source
 * @param {string} [newId]
 * @returns {import('./hangingProtocols').HangingProtocol|null}
 */
export function cloneProtocol(source, newId) {
  if (!source || !source.id) return null
  let raw
  try {
    raw = JSON.parse(JSON.stringify(source))
  } catch (e) {
    return null
  }
  delete raw._source
  const id = slugProtocolId(newId || `${source.id}-copy`)
  raw.id = id || `custom-${Date.now().toString(36)}`
  raw.label = String(raw.label || raw.id) + ' (copy)'
  if (typeof raw.priority === 'number') {
    raw.priority = Math.min(99, raw.priority + 1)
  }
  return normalizeProtocol(raw)
}

/**
 * @param {object|null|undefined} draft
 * @returns {import('./hangingProtocols').HangingProtocol|null}
 */
export function normalizeProtocol(draft) {
  if (!draft || typeof draft !== 'object') return null
  const id = slugProtocolId(draft.id)
  if (!id) return null
  const layoutIn = Array.isArray(draft.layout) ? draft.layout : [1, 1]
  const rows = Math.max(1, Math.min(4, Number(layoutIn[0]) || 1))
  const cols = Math.max(1, Math.min(4, Number(layoutIn[1]) || 1))
  const matchIn = draft.match && typeof draft.match === 'object' ? draft.match : {}
  const modalities = Array.isArray(matchIn.modalities)
    ? matchIn.modalities.map((m) => String(m).trim().toUpperCase()).filter(Boolean)
    : csvToList(matchIn.modalities).map((m) => m.toUpperCase())
  const studyDescription = Array.isArray(matchIn.studyDescription)
    ? matchIn.studyDescription.map((s) => String(s).trim()).filter(Boolean)
    : csvToList(matchIn.studyDescription)

  const slotsIn = Array.isArray(draft.slots) ? draft.slots : []
  const slots = slotsIn.map((slot, idx) => {
    const s = slot && typeof slot === 'object' ? slot : {}
    const seriesIn = s.series && typeof s.series === 'object' ? s.series : {}
    const role = s.role === 'prior' ? 'prior' : 'current'
    const series = {
      modality: seriesIn.modality
        ? String(seriesIn.modality).trim().toUpperCase()
        : null,
      description: Array.isArray(seriesIn.description)
        ? seriesIn.description.map((x) => String(x).trim()).filter(Boolean)
        : csvToList(seriesIn.description),
      exclude: Array.isArray(seriesIn.exclude)
        ? seriesIn.exclude.map((x) => String(x).trim()).filter(Boolean)
        : csvToList(seriesIn.exclude),
      requireDifferentPlane: !!seriesIn.requireDifferentPlane,
      usePriorMatching:
        role === 'prior'
          ? seriesIn.usePriorMatching !== false
          : !!seriesIn.usePriorMatching,
    }
    if (seriesIn.minScore != null && Number.isFinite(Number(seriesIn.minScore))) {
      series.minScore = Number(seriesIn.minScore)
    }
    const out = {
      viewportIndex:
        typeof s.viewportIndex === 'number' ? s.viewportIndex : idx,
      role,
      series,
    }
    if (role === 'prior' && typeof s.pairFromSlot === 'number') {
      out.pairFromSlot = s.pairFromSlot
    }
    return out
  })

  return {
    id,
    label: String(draft.label || id).trim() || id,
    priority:
      typeof draft.priority === 'number' && Number.isFinite(draft.priority)
        ? draft.priority
        : 50,
    match: { modalities, studyDescription },
    layout: [rows, cols],
    compareMode: !!draft.compareMode,
    linkSeries: draft.linkSeries !== false,
    slots: slots.length
      ? slots
      : createEmptyProtocol().slots.map((s) => ({ ...s, series: { ...s.series } })),
  }
}

/**
 * Seeds + custom; custom sobrescribe mismo id.
 * @param {object[]|null|undefined} customList
 * @returns {Array<object>}
 */
export function mergeHangingCatalog(customList) {
  /** @type {Map<string, object>} */
  const byId = new Map()
  const seeds = Array.isArray(HANGING_SEEDS) ? HANGING_SEEDS : []
  for (let i = 0; i < seeds.length; i++) {
    const p = seeds[i]
    if (!p || !p.id) continue
    byId.set(p.id, Object.assign({}, p, { _source: 'seed' }))
  }
  const custom = Array.isArray(customList) ? customList : []
  for (let i = 0; i < custom.length; i++) {
    const n = normalizeProtocol(custom[i])
    if (!n) continue
    byId.set(n.id, Object.assign({}, n, { _source: 'session' }))
  }
  return Array.from(byId.values()).sort((a, b) => {
    const pa = typeof a.priority === 'number' ? a.priority : 0
    const pb = typeof b.priority === 'number' ? b.priority : 0
    return pb - pa || String(a.id).localeCompare(String(b.id))
  })
}

/**
 * @param {string} protocolId
 * @param {object[]|null|undefined} customList
 * @returns {object|null}
 */
export function getProtocolFromCatalog(protocolId, customList) {
  const id = protocolId != null ? String(protocolId).trim() : ''
  if (!id) return null
  const all = mergeHangingCatalog(customList)
  for (let i = 0; i < all.length; i++) {
    if (all[i].id === id) {
      const copy = Object.assign({}, all[i])
      delete copy._source
      return copy
    }
  }
  return null
}

/**
 * @param {object[]|null|undefined} customList
 * @param {object} protocol
 * @returns {object[]}
 */
export function upsertCustomProtocol(customList, protocol) {
  const n = normalizeProtocol(protocol)
  if (!n) return Array.isArray(customList) ? customList.slice() : []
  const list = Array.isArray(customList) ? customList.slice() : []
  const idx = list.findIndex((p) => p && p.id === n.id)
  if (idx >= 0) list[idx] = n
  else list.push(n)
  return list
}

/**
 * @param {object[]|null|undefined} customList
 * @param {string} protocolId
 * @returns {object[]}
 */
export function removeCustomProtocol(customList, protocolId) {
  const id = protocolId != null ? String(protocolId).trim() : ''
  const list = Array.isArray(customList) ? customList : []
  if (!id) return list.slice()
  return list.filter((p) => !(p && p.id === id))
}

/**
 * ¿El id existe solo como seed (no custom)?
 * @param {string} protocolId
 * @param {object[]|null|undefined} customList
 */
export function isSeedOnlyProtocol(protocolId, customList) {
  const id = protocolId != null ? String(protocolId).trim() : ''
  if (!id) return false
  const custom = Array.isArray(customList) ? customList : []
  if (custom.some((p) => p && p.id === id)) return false
  return HANGING_SEEDS.some((p) => p && p.id === id)
}
